import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Save, 
  TrendingUp, 
  BarChart3,
  Target,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  X
} from 'lucide-react'
import Navigation from '../components/Navigation'

export default function DemoNewStrategyPage() {
  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(1)
  const [strategyData, setStrategyData] = useState({
    basic: {
      name: '',
      description: '',
      type: 'trend',
      market_symbol: 'BTC/USDT',
      timeframe: '1h',
      category: 'crypto'
    },
    parameters: {
      initial_capital: 10000,
      position_size: 0.1,
      stop_loss: 0.05,
      take_profit: 0.15,
      max_positions: 3,
      risk_per_trade: 0.02
    },
    logic: {
      entry_conditions: '',
      exit_conditions: '',
      risk_management: '',
      additional_notes: ''
    }
  })

  const handleInputChange = (section: string, field: string, value: any) => {
    setStrategyData(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value
      }
    }))
  }

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      trend: '趋势跟踪',
      grid: '网格交易',
      mean_reversion: '均值回归',
      breakout: '突破策略',
      scalping: '高频交易',
      other: '其他'
    }
    return typeMap[type] || type
  }

  const renderBasicInfoStep = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">基本信息</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              策略名称 *
            </label>
            <input
              type="text"
              value={strategyData.basic.name}
              onChange={(e) => handleInputChange('basic', 'name', e.target.value)}
              placeholder="输入策略名称"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              策略类型 *
            </label>
            <select
              value={strategyData.basic.type}
              onChange={(e) => handleInputChange('basic', 'type', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="trend">趋势跟踪</option>
              <option value="grid">网格交易</option>
              <option value="mean_reversion">均值回归</option>
              <option value="breakout">突破策略</option>
              <option value="scalping">高频交易</option>
              <option value="other">其他</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              交易对 *
            </label>
            <select
              value={strategyData.basic.market_symbol}
              onChange={(e) => handleInputChange('basic', 'market_symbol', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="BTC/USDT">BTC/USDT</option>
              <option value="ETH/USDT">ETH/USDT</option>
              <option value="BNB/USDT">BNB/USDT</option>
              <option value="ADA/USDT">ADA/USDT</option>
              <option value="SOL/USDT">SOL/USDT</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              时间周期 *
            </label>
            <select
              value={strategyData.basic.timeframe}
              onChange={(e) => handleInputChange('basic', 'timeframe', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="1m">1分钟</option>
              <option value="5m">5分钟</option>
              <option value="15m">15分钟</option>
              <option value="30m">30分钟</option>
              <option value="1h">1小时</option>
              <option value="4h">4小时</option>
              <option value="1d">1天</option>
            </select>
          </div>
        </div>
        
        <div className="mt-6">
          <label className="block text-sm font-medium text-ocean-secondary mb-2">
            策略描述
          </label>
          <textarea
            value={strategyData.basic.description}
            onChange={(e) => handleInputChange('basic', 'description', e.target.value)}
            placeholder="描述策略的核心逻辑和目标"
            rows={4}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-4">策略预览</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-ocean-secondary">策略类型</p>
                <p className="font-medium text-ocean-primary">{getTypeLabel(strategyData.basic.type)}</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <BarChart3 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-ocean-secondary">交易对</p>
                <p className="font-medium text-ocean-primary">{strategyData.basic.market_symbol}</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Target className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-ocean-secondary">时间周期</p>
                <p className="font-medium text-ocean-primary">{strategyData.basic.timeframe}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderParametersStep = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">参数配置</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              初始资金 (USDT)
            </label>
            <input
              type="number"
              value={strategyData.parameters.initial_capital}
              onChange={(e) => handleInputChange('parameters', 'initial_capital', parseFloat(e.target.value))}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              仓位大小 (%)
            </label>
            <input
              type="number"
              step="0.01"
              value={strategyData.parameters.position_size}
              onChange={(e) => handleInputChange('parameters', 'position_size', parseFloat(e.target.value))}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              止损比例 (%)
            </label>
            <input
              type="number"
              step="0.01"
              value={strategyData.parameters.stop_loss}
              onChange={(e) => handleInputChange('parameters', 'stop_loss', parseFloat(e.target.value))}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              止盈比例 (%)
            </label>
            <input
              type="number"
              step="0.01"
              value={strategyData.parameters.take_profit}
              onChange={(e) => handleInputChange('parameters', 'take_profit', parseFloat(e.target.value))}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              最大持仓数
            </label>
            <input
              type="number"
              value={strategyData.parameters.max_positions}
              onChange={(e) => handleInputChange('parameters', 'max_positions', parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              单笔风险 (%)
            </label>
            <input
              type="number"
              step="0.001"
              value={strategyData.parameters.risk_per_trade}
              onChange={(e) => handleInputChange('parameters', 'risk_per_trade', parseFloat(e.target.value))}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-4">风险提示</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3 p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-400">风险控制</p>
              <p className="text-sm text-ocean-secondary">请确保止损设置合理，避免单笔交易损失过大</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
            <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="font-medium text-blue-400">建议</p>
              <p className="text-sm text-ocean-secondary">建议在实盘前进行充分的回测验证</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderLogicStep = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">策略逻辑</h3>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              入场条件
            </label>
            <textarea
              value={strategyData.logic.entry_conditions}
              onChange={(e) => handleInputChange('logic', 'entry_conditions', e.target.value)}
              placeholder="描述策略的入场条件，例如：当RSI < 30且价格突破20日均线时买入"
              rows={4}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              出场条件
            </label>
            <textarea
              value={strategyData.logic.exit_conditions}
              onChange={(e) => handleInputChange('logic', 'exit_conditions', e.target.value)}
              placeholder="描述策略的出场条件，例如：达到止盈目标或触发止损时卖出"
              rows={4}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              风险管理
            </label>
            <textarea
              value={strategyData.logic.risk_management}
              onChange={(e) => handleInputChange('logic', 'risk_management', e.target.value)}
              placeholder="描述风险管理措施，例如：设置动态止损、仓位管理等"
              rows={4}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ocean-secondary mb-2">
              补充说明
            </label>
            <textarea
              value={strategyData.logic.additional_notes}
              onChange={(e) => handleInputChange('logic', 'additional_notes', e.target.value)}
              placeholder="其他需要说明的内容"
              rows={3}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-4">策略总结</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-ocean-secondary">初始资金</p>
                <p className="font-medium text-ocean-primary">${strategyData.parameters.initial_capital.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Target className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-ocean-secondary">仓位大小</p>
                <p className="font-medium text-ocean-primary">{strategyData.parameters.position_size * 100}%</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-ocean-secondary">止损比例</p>
                <p className="font-medium text-ocean-primary">{strategyData.parameters.stop_loss * 100}%</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-white/5 rounded-xl">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-ocean-secondary">止盈比例</p>
                <p className="font-medium text-ocean-primary">{strategyData.parameters.take_profit * 100}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return renderBasicInfoStep()
      case 2:
        return renderParametersStep()
      case 3:
        return renderLogicStep()
      default:
        return renderBasicInfoStep()
    }
  }

  const handleNext = () => {
    if (activeStep < 3) {
      setActiveStep(activeStep + 1)
    }
  }

  const handlePrev = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1)
    }
  }

  const handleSave = () => {
    // 这里可以添加保存逻辑
    navigate('/demo')
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      
      {/* 主要内容 */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面头部 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/demo')}
                className="btn-secondary flex items-center justify-center"
                title="返回主页"
              >
                <ArrowLeft className="w-4 h-4 mr-2 sm:mr-2" />
                <span className="hidden sm:inline">返回主页</span>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gradient">新建策略</h1>
                <p className="text-ocean-secondary">创建您的量化交易策略</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button 
              onClick={handleSave}
              className="btn-primary flex items-center justify-center"
              title="保存策略"
            >
              <Save className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">保存策略</span>
            </button>
          </div>
        </div>

        {/* 步骤指示器 */}
        <div className="card mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 ${
                activeStep >= 1 ? 'text-ocean-primary' : 'text-ocean-secondary'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep >= 1 ? 'bg-violet-500 text-white' : 'bg-white/10 text-ocean-secondary'
                }`}>
                  1
                </div>
                <span className="hidden sm:block">基本信息</span>
              </div>
              
              <div className={`w-8 h-1 rounded ${
                activeStep >= 2 ? 'bg-violet-500' : 'bg-white/10'
              }`}></div>
              
              <div className={`flex items-center space-x-2 ${
                activeStep >= 2 ? 'text-ocean-primary' : 'text-ocean-secondary'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep >= 2 ? 'bg-violet-500 text-white' : 'bg-white/10 text-ocean-secondary'
                }`}>
                  2
                </div>
                <span className="hidden sm:block">参数配置</span>
              </div>
              
              <div className={`w-8 h-1 rounded ${
                activeStep >= 3 ? 'bg-violet-500' : 'bg-white/10'
              }`}></div>
              
              <div className={`flex items-center space-x-2 ${
                activeStep >= 3 ? 'text-ocean-primary' : 'text-ocean-secondary'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeStep >= 3 ? 'bg-violet-500 text-white' : 'bg-white/10 text-ocean-secondary'
                }`}>
                  3
                </div>
                <span className="hidden sm:block">策略逻辑</span>
              </div>
            </div>
          </div>
        </div>

        {/* 步骤内容 */}
        {renderStepContent()}

        {/* 导航按钮 */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={handlePrev}
            disabled={activeStep === 1}
            className={`btn-secondary flex items-center justify-center ${
              activeStep === 1 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="上一步"
          >
            <ArrowLeft className="w-4 h-4 mr-2 sm:mr-2" />
            <span className="hidden sm:inline">上一步</span>
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/demo')}
              className="btn-secondary flex items-center justify-center"
              title="取消"
            >
              <X className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">取消</span>
            </button>
            
            {activeStep < 3 ? (
              <button
                onClick={handleNext}
                className="btn-primary flex items-center justify-center"
                title="下一步"
              >
                <span className="hidden sm:inline">下一步</span>
                <ArrowLeft className="w-4 h-4 ml-2 sm:ml-2 rotate-180" />
              </button>
            ) : (
              <button
                onClick={handleSave}
                className="btn-primary flex items-center justify-center"
                title="创建策略"
              >
                <Save className="w-4 h-4 mr-2 sm:mr-2" />
                <span className="hidden sm:inline">创建策略</span>
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  )
} 